import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Kiosk from "./pages/Kiosk";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import Roster from "./pages/Roster";
import Hours from "./pages/Hours";
import Leave from "./pages/Leave";
import Finance from "./pages/Finance";
import PosTables from "./pages/PosTables";
import PosMenu from "./pages/PosMenu";
import PosShifts from "./pages/PosShifts";
import Training from "./pages/Training";
import Suppliers from "./pages/Suppliers";
import SupplierCatalog from "./pages/SupplierCatalog";
import Stock from "./pages/Stock";
import Recipes from "./pages/Recipes";
import StockTake from "./pages/StockTake";
import Payroll from "./pages/Payroll";
import Settings from "./pages/Settings";
import ClientInvoices from "./pages/ClientInvoices";
import Functions from "./pages/Functions";
import AuditLog from "./pages/AuditLog";
import Permissions from "./pages/Permissions";
import Onboarding from "./pages/Onboarding";
import PublicOnboarding from "./pages/PublicOnboarding";
import Passe from "./pages/Passe";
import Landing from "./pages/Landing";
import Activate from "./pages/Activate";
import Admin from "./pages/Admin";
import MyPortal from "./pages/MyPortal";

function Loading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-2 bg-background">
      <p className="font-heading text-xl text-muted-foreground animate-pulse">MISE</p>
      <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60" data-testid="splash-tagline">Prepared Intelligence</p>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "staff" ? "/my" : "/"} replace />;
  }
  return children;
}

const MGR = ["owner", "manager"];

function PosProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (MGR.includes(user.role) || user.pos_operator) return children;
  return <Navigate to="/my" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/kiosk" element={<Kiosk />} />
          <Route path="/onboard/:token" element={<PublicOnboarding />} />
          <Route path="/activate/:token" element={<Activate />} />
          <Route path="/" element={<HomeSwitch />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/dashboard" element={<Protected roles={MGR}><Dashboard /></Protected>} />
            <Route path="/employees" element={<Protected roles={MGR}><Employees /></Protected>} />
            <Route path="/employees/:id" element={<Protected roles={MGR}><EmployeeDetail /></Protected>} />
            <Route path="/onboarding" element={<Protected roles={MGR}><Onboarding /></Protected>} />
            <Route path="/passe" element={<Protected><Passe /></Protected>} />
            <Route path="/roster" element={<Protected roles={MGR}><Roster /></Protected>} />
            <Route path="/hours" element={<Protected roles={MGR}><Hours /></Protected>} />
            <Route path="/leave" element={<Protected roles={MGR}><Leave /></Protected>} />
            <Route path="/finance" element={<Protected roles={MGR}><Finance /></Protected>} />
            <Route path="/client-invoices" element={<Protected roles={MGR}><ClientInvoices /></Protected>} />
            <Route path="/functions" element={<Protected roles={MGR}><Functions /></Protected>} />
            <Route path="/revenue" element={<Navigate to="/finance?tab=revenue" replace />} />
            <Route path="/invoices" element={<Navigate to="/finance?tab=expenses" replace />} />
            <Route path="/fixed-expenses" element={<Navigate to="/finance?tab=expenses&esub=fixed" replace />} />
            <Route path="/sales-count" element={<Navigate to="/finance?tab=sales" replace />} />
            <Route path="/pos" element={<PosProtected><PosTables /></PosProtected>} />
            <Route path="/pos/menu" element={<Protected roles={MGR}><PosMenu /></Protected>} />
            <Route path="/pos/shifts" element={<Protected roles={MGR}><PosShifts /></Protected>} />
            <Route path="/training" element={<Protected roles={MGR}><Training /></Protected>} />
            <Route path="/suppliers" element={<Protected roles={MGR}><Suppliers /></Protected>} />
            <Route path="/suppliers/:supplierId/catalog" element={<Protected roles={MGR}><SupplierCatalog /></Protected>} />
            <Route path="/stock" element={<Protected roles={MGR}><Stock /></Protected>} />
            <Route path="/recipes" element={<Protected roles={MGR}><Recipes /></Protected>} />
            <Route path="/stocktake" element={<Protected roles={MGR}><StockTake /></Protected>} />
            <Route path="/payroll" element={<Protected roles={MGR}><Payroll /></Protected>} />
            <Route path="/settings" element={<Protected roles={["owner"]}><Settings /></Protected>} />
            <Route path="/permissions" element={<Protected roles={["owner"]}><Permissions /></Protected>} />
            <Route path="/audit" element={<Protected roles={MGR}><AuditLog /></Protected>} />
            <Route path="/admin" element={<Protected roles={MGR}><Admin /></Protected>} />
            <Route path="/my" element={<MyPortal />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

function HomeSwitch() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

export default App;
